import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '@crm/shared';
import { toast } from 'react-toastify';
import { fmtDate } from '../utils/dateUtils';
import {
  FiBarChart2, FiRefreshCw, FiDownload, FiSearch,
  FiAlertCircle, FiCalendar, FiDollarSign,
  FiFileText, FiCheckCircle,
  FiTrendingUp, FiUsers, FiActivity, FiFilter, FiX,
  FiChevronLeft, FiChevronRight, FiPhone, FiTarget,
} from 'react-icons/fi';

/* ─── Design Tokens (matches rest of app) ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyM:'#1A5276', navyL:'#EBF4FF',
  gold:'#E8930A', goldL:'#FEF3DC', goldB:'#F5A623',
  green:'#15803D', greenL:'#DCFCE7', greenB:'#16A34A',
  sky:'#0369A1', skyL:'#DBEAFE', skyB:'#0284C7',
  purple:'#6D28D9', purpleL:'#EDE9FE', purpleB:'#7C3AED',
  teal:'#0D9488', tealL:'#CCFBF1', tealB:'#5EEAD4',
  red:'#B91C1C', redL:'#FEF2F2', redB:'#DC2626', redBord:'#FECACA',
  border:'#E5E7EB', borderL:'#F3F4F6',
  bg:'#F4F6F9', white:'#FFFFFF',
  t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Helpers ─── */
const fmtAmt  = n  => (n != null && n !== '' && n !== 0) ? '₹' + Number(n).toLocaleString('en-IN') : '₹0';
const PAGE_SIZE = 20;

const now    = new Date();
const lmDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
const lmName = lmDate.toLocaleString('en-IN', { month:'long', year:'numeric' });

/* ─── Current month default ─── */
const curYear  = now.getFullYear();
const curMonth = now.getMonth() + 1; // 1-based

/* ─── Shimmer ─── */
const Sh = ({ h=20, w='100%', r=6, mb=0 }) => (
  <div style={{ height:h, width:w, borderRadius:r, marginBottom:mb,
    background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
    backgroundSize:'400px 100%', animation:'rptShimmer 1.4s infinite' }}/>
);

/* ─── Pill ─── */
const Pill = ({ label, bg, color, border }) => (
  <span style={{ display:'inline-flex', alignItems:'center', padding:'2px 10px', borderRadius:999,
    fontSize:11, fontWeight:700, background:bg, color, border:`1px solid ${border||bg}`, whiteSpace:'nowrap' }}>
    {label}
  </span>
);

/* ─── KPI Card ─── */
const Kpi = ({ label, value, icon, iconBg, iconColor, accent, loading, sub, onClick }) => (
  <div onClick={onClick} style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
    padding:'16px 18px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)', position:'relative', overflow:'hidden',
    cursor: onClick ? 'pointer' : 'default' }}>
    <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:accent, borderRadius:'12px 12px 0 0' }}/>
    <div style={{ width:36, height:36, borderRadius:9, background:iconBg,
      display:'flex', alignItems:'center', justifyContent:'center', color:iconColor, marginBottom:10 }}>{icon}</div>
    {loading ? <Sh h={28} w="50%" mb={6}/> :
      <div style={{ fontSize:24, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1, marginBottom:5 }}>
        {value ?? '—'}
      </div>}
    <div style={{ fontSize:12, fontWeight:600, color:C.t3 }}>{label}</div>
    {sub && !loading && <div style={{ fontSize:11, color:C.t4, marginTop:3 }}>{sub}</div>}
  </div>
);

/* ─── Error state ─── */
const Err = ({ msg, onRetry }) => (
  <div style={{ padding:'32px 24px', textAlign:'center' }}>
    <div style={{ background:C.redL, border:`1px solid ${C.redBord}`, borderRadius:12,
      padding:'18px 22px', display:'inline-block', maxWidth:480, textAlign:'left' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <FiAlertCircle size={15} style={{ color:C.redB }}/>
        <span style={{ fontWeight:700, color:C.redB, fontSize:13 }}>Could not load data</span>
      </div>
      <p style={{ color:'#7F1D1D', fontSize:12.5, margin:'0 0 12px' }}>{msg}</p>
      {onRetry && (
        <button onClick={onRetry}
          style={{ background:C.redB, color:C.white, border:'none', borderRadius:8,
            padding:'7px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
          Retry
        </button>
      )}
    </div>
  </div>
);

/* ─── Empty state ─── */
const Empty = ({ label = 'No data available' }) => (
  <div style={{ textAlign:'center', padding:'56px 24px' }}>
    <div style={{ width:52, height:52, borderRadius:'50%', background:C.navyL,
      display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
      <FiCalendar size={22} style={{ color:C.navy, opacity:0.4 }}/>
    </div>
    <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>{label}</div>
    <div style={{ fontSize:12.5, color:C.t3 }}>Data will appear here once available</div>
  </div>
);

/* ─── Stage Detail Modal ─── */
function StageDetailModal({ stage, stageLabel, onClose }) {
  const [cases, setCases]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [page, setPage]       = useState(1);
  const PAGE = 20;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // 1. Fetch all leads for this stage
      const all = [];
      let pg = 1;
      while (true) {
        const res = await api.getLeads({ page: pg, page_size: 100, current_stage: stage });
        const d = res?.data;
        const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
        all.push(...results);
        if (!d?.next || results.length === 0) break;
        pg++;
        if (pg > 20) break;
      }
      // client-side fallback filter in case backend ignores the param
      const filtered = all.filter(l =>
        !stage || (l.current_stage || 'FOLLOW_UP') === stage
      );

      // 2. Fetch login + disbursement records to enrich fields not on lead list API
      const [loginRes, disbRes] = await Promise.allSettled([
        api.getLeadLogins({ page_size: 1000 }),
        api.getLeadDisbursements({ page_size: 1000 }),
      ]);

      // Build login map: lead_id → login record
      const loginMap = {};
      const loginData = loginRes.status === 'fulfilled'
        ? (loginRes.value?.data?.results || loginRes.value?.data || [])
        : [];
      loginData.forEach(r => { if (r.lead) loginMap[String(r.lead)] = r; });

      // Build disbursement map: lead_id → { latest date, total amount }
      const disbMap = {};
      const disbData = disbRes.status === 'fulfilled'
        ? (disbRes.value?.data?.results || disbRes.value?.data || [])
        : [];
      disbData.forEach(d => {
        const key = String(d.lead);
        if (!disbMap[key]) disbMap[key] = { date: null, amount: 0 };
        if (d.disbursement_date && (!disbMap[key].date || d.disbursement_date > disbMap[key].date)) {
          disbMap[key].date = d.disbursement_date;
        }
        disbMap[key].amount += parseFloat(d.amount || 0);
      });

      // 3. Enrich each lead with login/disbursement data
      const enriched = filtered.map(l => {
        const key = String(l.id);
        const lr = loginMap[key];
        const dr = disbMap[key];
        return {
          ...l,
          _institution_name: lr?.institution_detail?.name || lr?.institution_name || l.institution?.name || '',
          _login_date: lr?.login_date || l.login_date || '',
          _disbursement_date: dr?.date || l.disbursement_date || '',
          _disbursed_amount: dr?.amount || l.disbursed_amount || 0,
        };
      });

      setCases(enriched);
    } catch (e) {
      setError(e?.message || String(e) || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, [stage]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(cases.length / PAGE));
  const visible    = cases.slice((page - 1) * PAGE, page * PAGE);

  const stageMap = {
    FOLLOW_UP:'Follow-up', LOGIN:'Login', SANCTION:'Sanction',
    DISBURSEMENT:'Disbursement', PDD:'PDD', REVENUE:'Revenue', CLOSED:'Closed',
  };

  // close on backdrop click
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div onClick={handleBackdrop} style={{
      position:'fixed', inset:0, background:'rgba(13,27,42,0.55)', zIndex:9999,
      display:'flex', alignItems:'flex-start', justifyContent:'center',
      padding:'40px 16px', overflowY:'auto',
    }}>
      <div style={{
        background:C.white, borderRadius:14, width:'100%', maxWidth:960,
        boxShadow:'0 20px 60px rgba(0,0,0,0.28)', flexShrink:0,
      }}>
        {/* Header */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'14px 20px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC',
          borderRadius:'14px 14px 0 0',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <FiFileText size={15} style={{ color:C.navy }}/>
            <span style={{ fontSize:14, fontWeight:800, color:C.t1 }}>
              {stageLabel} — Case-wise Details
            </span>
            {!loading && (
              <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px',
                borderRadius:999, fontSize:10.5, fontWeight:700, border:`1px solid rgba(13,59,94,0.12)` }}>
                {cases.length} cases
              </span>
            )}
          </div>
          <button onClick={onClose} style={{
            width:32, height:32, borderRadius:8, border:`1px solid ${C.border}`,
            background:C.white, color:C.t3, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}><FiX size={14}/></button>
        </div>

        {/* Body */}
        <div style={{ padding:18 }}>
          {loading && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[0,1,2,3,4].map(i => <Sh key={i} h={44}/>)}
            </div>
          )}
          {!loading && error && <Err msg={error} onRetry={load}/>}
          {!loading && !error && cases.length === 0 && <Empty label={`No cases found for ${stageLabel}`}/>}
          {!loading && !error && cases.length > 0 && (
            <>
              <div style={{ overflowX:'auto', borderRadius:10, border:`1px solid ${C.border}` }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {['#','Lead No','Customer','Mobile','Product','Institution','Stage','Login Date','Disb. Date','Disbursed Amt','Executive'].map((h,i) => (
                        <th key={h} style={{
                          padding:'9px 13px', fontSize:10.5, fontWeight:800, color:C.t2,
                          textTransform:'uppercase', letterSpacing:'0.07em',
                          textAlign: i >= 9 ? 'right' : 'left',
                          borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((l, idx) => {
                      const isLast = idx === visible.length - 1;
                      const exec = [l.created_by_detail?.first_name, l.created_by_detail?.last_name]
                        .filter(Boolean).join(' ') || l.created_by_name || '—';
                      return (
                        <tr key={l.id} style={{ background: idx % 2 === 0 ? C.white : '#FAFBFC' }}>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t4 }}>
                            {(page-1)*PAGE + idx + 1}
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <span style={{ fontSize:12, fontWeight:700, color:C.navy }}>{l.lead_no || `#${l.id}`}</span>
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, fontWeight:600, color:C.t1 }}>
                            {l.customer_name || '—'}
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>
                            {l.mobile_number || '—'}
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>
                            {l.product?.name || '—'}
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>
                            {l._institution_name || '—'}
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <span style={{
                              fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                              background: l.current_stage === 'DISBURSEMENT' ? C.greenL
                                : l.current_stage === 'SANCTION' ? C.skyL
                                : l.current_stage === 'PDD' ? C.navyL
                                : l.current_stage === 'REVENUE' ? C.tealL
                                : l.current_stage === 'CLOSED' ? '#F1F5F9'
                                : C.goldL,
                              color: l.current_stage === 'DISBURSEMENT' ? C.greenB
                                : l.current_stage === 'SANCTION' ? C.skyB
                                : l.current_stage === 'PDD' ? C.navy
                                : l.current_stage === 'REVENUE' ? C.tealB
                                : l.current_stage === 'CLOSED' ? '#64748B'
                                : C.gold,
                            }}>
                              {stageMap[l.current_stage] || l.current_stage || 'Follow-up'}
                            </span>
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}>
                            {l._login_date ? fmtDate(l._login_date) : '—'}
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}>
                            {l._disbursement_date ? fmtDate(l._disbursement_date) : '—'}
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:800, color:C.greenB }}>
                              {l._disbursed_amount ? fmtAmt(l._disbursed_amount) : '—'}
                            </span>
                          </td>
                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>
                            {exec}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  marginTop:12, padding:'8px 4px' }}>
                  <span style={{ fontSize:12, color:C.t3 }}>
                    Showing {(page-1)*PAGE+1}–{Math.min(page*PAGE, cases.length)} of {cases.length}
                  </span>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                      style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                        background:page===1?C.borderL:C.white, color:page===1?C.t4:C.t2,
                        cursor:page===1?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <FiChevronLeft size={13}/>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i+1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                      .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i-1] > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) => p === '…'
                        ? <span key={`ellipsis-${i}`} style={{ padding:'0 4px', color:C.t4, lineHeight:'32px' }}>…</span>
                        : <button key={p} onClick={() => setPage(p)}
                            style={{ width:32, height:32, borderRadius:7,
                              border:`1px solid ${p===page ? C.navy : C.border}`,
                              background:p===page ? C.navy : C.white,
                              color:p===page ? '#fff' : C.t2,
                              cursor:'pointer', fontSize:12, fontWeight:p===page?700:400 }}>{p}</button>
                      )}
                    <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}
                      style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                        background:page===totalPages?C.borderL:C.white, color:page===totalPages?C.t4:C.t2,
                        cursor:page===totalPages?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <FiChevronRight size={13}/>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Section Card ─── */
const Card = ({ title, icon, badge, children, headerRight }) => (
  <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
    overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
      padding:'13px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        {icon && <span style={{ color:C.navy, opacity:0.65, display:'flex' }}>{icon}</span>}
        <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>{title}</span>
        {badge != null && (
          <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:999,
            fontSize:10.5, fontWeight:700, border:`1px solid rgba(13,59,94,0.12)` }}>{badge}</span>
        )}
      </div>
      {headerRight}
    </div>
    <div style={{ padding:18 }}>{children}</div>
  </div>
);

/* ─── CSV export ─── */
const exportCsv = (headers, rows, filename) => {
  const csv = [headers, ...rows]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(','))
    .join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([csv], { type:'text/csv' })),
    download: `${filename}_${new Date().toISOString().slice(0,10)}.csv`,
  });
  a.click(); URL.revokeObjectURL(a.href);
};

/* ─── Pipeline bar ─── */
const PBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <span style={{ fontSize:12.5, fontWeight:500, color:C.t2 }}>{label}</span>
        <div style={{ display:'flex', gap:8 }}>
          <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>{value}</span>
          <span style={{ fontSize:11, color:C.t4, minWidth:30, textAlign:'right' }}>{pct}%</span>
        </div>
      </div>
      <div style={{ height:6, background:'#F1F5F9', borderRadius:999, overflow:'hidden' }}>
        <div style={{ height:'100%', background:color, borderRadius:999, width:`${pct}%`,
          minWidth: value > 0 ? 4 : 0, transition:'width 0.7s ease-out' }}/>
      </div>
    </div>
  );
};

/* ═════════════════════════════════════════════════════════════════
   TAB 1 — LAST MONTH MIS
   Real API: GET /api/leads/mis/last_month/
   Response: { "last_month_cases": 0 }
   (Returns a summary count, not a paginated list.)
═════════════════════════════════════════════════════════════════ */
function LastMonthMIS() {
  const [data, setData]       = useState(null);   // { last_month_cases: number }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getMisLastMonth();
      const d = res?.data;
      setData(d);
    } catch (e) {
      setError(e?.message || e?.detail || String(e) || 'Failed to load last month MIS');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cases = data?.last_month_cases ?? 0;

  const handleExport = async () => {
    setExporting(true);
    try {
      const allLeads = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await api.getLeads({ page, page_size: 100 });
        const d = res?.data;
        const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
        allLeads.push(...results);
        hasMore = !!d?.next && results.length > 0;
        page++;
      }

      const lmStart = new Date(lmDate.getFullYear(), lmDate.getMonth(), 1);
      const lmEnd = new Date(lmDate.getFullYear(), lmDate.getMonth() + 1, 0, 23, 59, 59);

      const monthLeads = allLeads.filter(l => {
        const d = l.disbursement_date || l.login_date || l.created_at;
        if (!d) return false;
        const dt = new Date(d);
        return dt >= lmStart && dt <= lmEnd;
      });

      const stageMap = {
        FOLLOW_UP: 'Follow-up', LOGIN: 'Login', SANCTION: 'Sanction',
        DISBURSEMENT: 'Disbursement', PDD: 'PDD', REVENUE: 'Revenue', CLOSED: 'Closed',
      };

      exportCsv(
        ['Lead No','Customer','Mobile','Product','Lead Source','BSA Code','Institution','Channel','Banker','Stage','Login Date','Sanction Date','Expected Disb Date','Disbursement Date','Disbursed Amount','Sanctioned Amount','Payout to Company','Revenue Received','Invoice No','Executive','Remarks'],
        monthLeads.map(l => {
          const exec = [l.created_by_detail?.first_name, l.created_by_detail?.last_name].filter(Boolean).join(' ')
            || l.created_by_name || '—';
          return [
            l.lead_no || `#${l.id}`,
            l.customer_name || '',
            l.mobile_number || '',
            l.product?.name || '',
            l.lead_source || '',
            l.bsa?.code || '',
            l.institution?.name || '',
            l.channel?.name || '',
            l.banker_name || '',
            stageMap[l.current_stage] || l.status?.name || '',
            l.login_date || '',
            l.sanction_date || '',
            l.expected_disbursement_date || '',
            l.disbursement_date || '',
            l.disbursed_amount || '',
            l.sanctioned_amount || '',
            l.payout_to_company || '',
            l.revenue_received || '',
            l.invoice_number || '',
            exec,
            l.remarks || '',
          ];
        }),
        `last_month_mis_${lmDate.getFullYear()}_${lmDate.getMonth()+1}`
      );
    } catch (e) {
      toast.error('Failed to export last month data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div style={{ background:C.skyL, border:`1px solid #BAE6FD`, borderRadius:10,
        padding:'10px 16px', display:'flex', alignItems:'flex-start', gap:9, marginBottom:14 }}>
        <FiFileText size={13} style={{ color:C.sky, marginTop:1, flexShrink:0 }}/>
        <span style={{ fontSize:12.5, color:C.sky, fontWeight:500, lineHeight:1.55 }}>
          <strong>Last Month MIS:</strong> Total disbursed/processed cases from <strong>{lmName}</strong>.
          This is a read-only monthly summary.
        </span>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
        <Kpi label="Total Cases Last Month" value={loading ? null : cases}
          icon={<FiFileText size={15}/>} iconBg={C.navyL} iconColor={C.navy}
          accent={C.navy} loading={loading} sub={lmName}/>
        <Kpi label="Month" value={loading ? null : lmDate.toLocaleString('en-IN',{month:'short'})}
          icon={<FiCalendar size={15}/>} iconBg={C.skyL} iconColor={C.skyB}
          accent={C.skyB} loading={loading} sub={String(lmDate.getFullYear())}/>
        <Kpi label="Status" value={loading ? null : (cases > 0 ? 'Data Available' : 'No Cases')}
          icon={<FiCheckCircle size={15}/>} iconBg={cases > 0 ? C.greenL : C.borderL}
          iconColor={cases > 0 ? C.greenB : C.t3}
          accent={cases > 0 ? C.greenB : '#94A3B8'} loading={loading}/>
      </div>

      <Card title={`Last Month Summary — ${lmName}`} icon={<FiBarChart2 size={13}/>}
        headerRight={
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={load}
              style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                background:C.white, color:C.t3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <FiRefreshCw size={13}/>
            </button>
            <button onClick={handleExport} disabled={exporting}
              style={{ height:32, padding:'0 14px', borderRadius:7, background:exporting ? C.border : C.goldB,
                color:exporting ? C.t4 : '#0D1B2A', border:'none', fontSize:12, fontWeight:700,
                cursor:exporting ? 'not-allowed' : 'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', gap:5 }}>
              {exporting
                ? <><div style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(13,27,42,0.3)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} /> Exporting…</>
                : <><FiDownload size={11}/> Export CSV</>}
            </button>
          </div>
        }>
        {loading && <div style={{ display:'flex', flexDirection:'column', gap:10 }}>{[0,1,2].map(i=><Sh key={i} h={40}/>)}</div>}
        {!loading && error && <Err msg={error} onRetry={load}/>}
        {!loading && !error && (
          <div>
            {/* Summary row */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <div style={{ background:C.navyL, borderRadius:10, padding:'16px 18px', borderTop:`3px solid ${C.navy}` }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 }}>Total Cases</div>
                <div style={{ fontSize:28, fontWeight:800, color:C.t1, letterSpacing:'-0.04em' }}>{cases}</div>
                <div style={{ fontSize:11.5, color:C.t3, marginTop:4 }}>{lmName}</div>
              </div>
              <div style={{ background:cases > 0 ? C.greenL : C.borderL, borderRadius:10, padding:'16px 18px',
                borderTop:`3px solid ${cases > 0 ? C.greenB : '#94A3B8'}` }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 }}>Summary</div>
                <div style={{ fontSize:14, fontWeight:700, color:cases > 0 ? C.greenB : C.t3 }}>
                  {cases > 0 ? `${cases} case${cases !== 1 ? 's' : ''} processed` : 'No cases recorded'}
                </div>
                <div style={{ fontSize:11.5, color:C.t3, marginTop:4 }}>for {lmName}</div>
              </div>
            </div>

            {/* Note about API */}
            <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:9, padding:'12px 14px',
              fontSize:12.5, color:'#92400E', lineHeight:1.55 }}>
              <strong>ℹ API Note:</strong> The <code>/api/leads/mis/last_month/</code> endpoint currently returns a case count summary.
              Detailed case breakdown will be available when the backend exposes individual records.
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   TAB 2 — OVERALL MIS
   Real API: GET /api/leads/mis/overall/?year=YYYY&month=MM
   Response: { "detail": "year and month required" } if missing params
   Once params supplied → actual response shape TBD; handled gracefully.
═════════════════════════════════════════════════════════════════ */
function OverallMIS() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [drill, setDrill]       = useState(null); // { stage, label }
  const [year,  setYear]        = useState(String(curYear));
  const [month, setMonth]       = useState(String(curMonth));
  const [searched, setSearched] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (y = year, m = month) => {
    if (!y || !m) { setError('Please select both year and month.'); return; }
    setLoading(true); setError(null); setSearched(true);
    try {
      // API requires year and month as query params
      const res = await api.getMisOverall({ year: y, month: Number(m) });
      const d   = res?.data;
      // Handle backend error object gracefully
      if (d?.detail) throw new Error(d.detail);
      setData(d);
    } catch (e) {
      setError(e?.message || e?.detail || String(e) || 'Failed to load overall MIS');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  // Derive display values — API returns flat summary object:
  // { total_leads, login_stage, sanction_stage, disbursement_stage,
  //   pdd_stage, revenue_stage, closed_stage,
  //   total_disbursement, total_revenue_received }
  const rows      = Array.isArray(data?.results) ? data.results
                  : Array.isArray(data?.data)    ? data.data
                  : Array.isArray(data)           ? data
                  : [];

  // Flat summary fields (API returns user-wise aggregated data in results array)
  const totalCount  = rows.reduce((s,r) => s + Number(r.total_leads||0), 0);
  const totalDisb   = rows.reduce((s,r) => s + Number(r.total_disbursement||0), 0);
  const totalRev    = rows.reduce((s,r) => s + Number(r.total_revenue||0), 0);

  // Stage-wise counts from rows (user-wise aggregated)
  const loginStage  = rows.reduce((s,r) => s + Number(r.login_stage||0), 0);
  const sancStage   = rows.reduce((s,r) => s + Number(r.sanction_stage||0), 0);
  const disbStage   = rows.reduce((s,r) => s + Number(r.disbursement_stage||0), 0);
  const pddStage    = rows.reduce((s,r) => s + Number(r.pdd_stage||0), 0);
  const revStage    = rows.reduce((s,r) => s + Number(r.revenue_stage||0), 0);
  const closedStage = rows.reduce((s,r) => s + Number(r.closed_stage||0), 0);

  const monthLabel = year && month
    ? new Date(`${year}-${month}-01`).toLocaleString('en-IN', { month:'long', year:'numeric' })
    : '';

  const YEARS  = Array.from({ length: 5 }, (_, i) => String(curYear - i));
  const MONTHS = [
    { v:'1', l:'January'   }, { v:'2', l:'February'  }, { v:'3', l:'March'     },
    { v:'4', l:'April'     }, { v:'5', l:'May'        }, { v:'6', l:'June'      },
    { v:'7', l:'July'      }, { v:'8', l:'August'     }, { v:'9', l:'September' },
    { v:'10',l:'October'   }, { v:'11',l:'November'   }, { v:'12',l:'December'  },
  ];

  const doExport = async () => {
    if (!year || !month) { toast.error('Please select year and month first'); return; }
    setExporting(true);
    try {
      const yNum = Number(year);
      const mNum = Number(month);
      const monthStart = new Date(yNum, mNum - 1, 1);
      const monthEnd   = new Date(yNum, mNum, 0, 23, 59, 59);

      // Fetch leads + login + disbursement + revenue records in parallel
      const leadsPromise = (async () => {
        const all = [];
        let pg = 1;
        while (true) {
          const res = await api.getLeads({ page: pg, page_size: 100 });
          const d = res?.data;
          const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
          all.push(...results);
          if (!d?.next || results.length === 0) break;
          pg++;
          if (pg > 20) break;
        }
        return all;
      })();

      const [allLeads, loginRes, disbRes, revRes] = await Promise.all([
        leadsPromise,
        api.getLeadLogins({ page_size: 1000 }),
        api.getLeadDisbursements({ page_size: 1000 }),
        api.getLeadRevenues({ page_size: 1000 }),
      ]);

      // Login map: lead_id → login record
      const loginMap = {};
      (loginRes?.data?.results || loginRes?.data || []).forEach(r => {
        if (r.lead) loginMap[String(r.lead)] = r;
      });

      // Disbursement map: lead_id → { latest date, total amount }
      const disbMap = {};
      (disbRes?.data?.results || disbRes?.data || []).forEach(d => {
        const key = String(d.lead);
        if (!disbMap[key]) disbMap[key] = { date: '', amount: 0 };
        if (d.disbursement_date && (!disbMap[key].date || d.disbursement_date > disbMap[key].date))
          disbMap[key].date = d.disbursement_date;
        disbMap[key].amount += parseFloat(d.amount || 0);
      });

      // Revenue map: lead_id → revenue record
      const revMap = {};
      (revRes?.data?.results || revRes?.data || []).forEach(r => {
        if (r.lead) revMap[String(r.lead)] = r;
      });

      // Filter by selected month
      const monthLeads = allLeads.filter(l => {
        const dateStr = l.disbursement_date || l.login_date || l.created_at;
        if (!dateStr) return false;
        const dt = new Date(dateStr);
        return dt >= monthStart && dt <= monthEnd;
      });

      if (monthLeads.length === 0) {
        toast.info(`No cases found for ${monthLabel}`);
        return;
      }

      const stageMap = {
        FOLLOW_UP: 'Follow-up', LOGIN: 'Login', SANCTION: 'Sanction',
        DISBURSEMENT: 'Disbursement', PDD: 'PDD', REVENUE: 'Revenue', CLOSED: 'Closed',
      };

      exportCsv(
        ['#','Lead No','Customer','Mobile','Product','Lead Source','BSA Code','Institution','Channel','Banker','Stage','Login Date','Sanction Date','Expected Disb Date','Disbursement Date','Disbursed Amount','Sanctioned Amount','Payout to Company','Revenue Received','Invoice No','Executive','Remarks'],
        monthLeads.map((l, i) => {
          const key = String(l.id);
          const lr  = loginMap[key];
          const dr  = disbMap[key];
          const rr  = revMap[key];
          const exec = [l.created_by_detail?.first_name, l.created_by_detail?.last_name]
            .filter(Boolean).join(' ') || l.created_by_name || '—';
          return [
            i + 1,
            l.lead_no || `#${l.id}`,
            l.customer_name || '',
            l.mobile_number || '',
            l.product?.name || '',
            l.lead_source || '',
            l.bsa?.code || l.bsa_code || '',
            lr?.institution_detail?.name || l.institution?.name || '',
            lr?.channel_detail?.name || l.channel?.name || '',
            lr?.banker_detail?.name || l.banker_name || '',
            stageMap[l.current_stage] || l.status?.name || '',
            lr?.login_date || '',
            l.sanction_date || '',
            l.expected_disbursement_date || '',
            dr?.date || '',
            dr?.amount || '',
            l.sanctioned_amount || '',
            rr?.payout_to_company || '',
            rr?.revenue_received || '',
            rr?.invoice_number || '',
            exec,
            l.remarks || '',
          ];
        }),
        `overall_mis_${year}_${month}`
      );
    } catch (e) {
      toast.error('Failed to export MIS data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {/* Param selector */}
      <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
        padding:'16px 20px', marginBottom:14, boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
        <div style={{ fontSize:11.5, fontWeight:700, color:C.t3, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
          Select Period
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Year</label>
            <select value={year} onChange={e => setYear(e.target.value)}
              style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
                fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit', minWidth:100 }}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Month</label>
            <select value={month} onChange={e => setMonth(e.target.value)}
              style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
                fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit', minWidth:140 }}>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <button onClick={() => load(year, month)}
            style={{ height:36, padding:'0 20px', borderRadius:8, background:C.navy, color:'#fff',
              border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
              display:'flex', alignItems:'center', gap:6 }}>
            <FiFilter size={12}/> Load Report
          </button>
          {searched && !loading && (
            <button onClick={doExport} disabled={exporting}
              style={{ height:36, padding:'0 16px', borderRadius:8,
                background: exporting ? C.border : C.goldB,
                color: exporting ? C.t4 : '#0D1B2A',
                border:'none', fontSize:13, fontWeight:700,
                cursor: exporting ? 'not-allowed' : 'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', gap:6, opacity: exporting ? 0.7 : 1 }}>
              {exporting
                ? <><div style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(13,27,42,0.3)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }}/> Exporting…</>
                : <><FiDownload size={12}/> Export CSV</>}
            </button>
          )}
        </div>
      </div>

      {/* Not yet searched */}
      {!searched && !loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'48px 24px', textAlign:'center', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ width:52, height:52, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
            <FiBarChart2 size={22} style={{ color:C.navy, opacity:0.5 }}/>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>Select a period to load the Overall MIS</div>
          <div style={{ fontSize:12.5, color:C.t3 }}>Choose year and month, then click "Load Report"</div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[0,1,2,3,4].map(i => <Sh key={i} h={48}/>)}
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && <Err msg={error} onRetry={() => load(year, month)}/>}

      {/* Results */}
      {!loading && !error && searched && data !== null && (
        <>
          {/* KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            <Kpi label="Total Leads"         value={totalCount}        icon={<FiFileText size={15}/>}    iconBg={C.navyL}  iconColor={C.navy}  accent={C.navy}   loading={false} sub={monthLabel}/>
            <Kpi label="Total Disbursement"  value={fmtAmt(totalDisb)} icon={<FiDollarSign size={15}/>}  iconBg={C.greenL} iconColor={C.greenB} accent={C.greenB} loading={false}/>
            <Kpi label="Total Revenue"       value={fmtAmt(totalRev)}  icon={<FiTrendingUp size={15}/>}  iconBg={C.tealL}  iconColor={C.tealB} accent={C.tealB}  loading={false}/>
            <Kpi label="Closed Leads"        value={closedStage}       icon={<FiCheckCircle size={15}/>} iconBg="#F1F5F9"  iconColor="#64748B" accent="#94A3B8"  loading={false}/>
          </div>

          {/* Summary display — API returns flat aggregate object */}
          {rows.length === 0 && (
            <>
              {totalCount === 0 ? (
                <Card title={`Overall MIS — ${monthLabel}`} icon={<FiBarChart2 size={13}/>}>
                  <Empty label={`No records found for ${monthLabel}`}/>
                </Card>
              ) : (
                <>
                  {/* Stage-wise pipeline breakdown */}
                  <Card title={`Stage-wise Breakdown — ${monthLabel}`} icon={<FiBarChart2 size={13}/>}
                    headerRight={
                      <button onClick={() => load(year, month)}
                        style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                          background:C.white, color:C.t3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <FiRefreshCw size={13}/>
                      </button>
                    }>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
                      {[
                        { label:'Login',        value:loginStage,  color:C.purple, bg:C.purpleL, stage:'LOGIN' },
                        { label:'Sanction',     value:sancStage,   color:C.skyB,   bg:C.skyL,    stage:'SANCTION' },
                        { label:'Disbursement', value:disbStage,   color:C.greenB, bg:C.greenL,  stage:'DISBURSEMENT' },
                        { label:'PDD',          value:pddStage,    color:C.navy,   bg:C.navyL,   stage:'PDD' },
                        { label:'Revenue',      value:revStage,    color:C.tealB,  bg:C.tealL,   stage:'REVENUE' },
                        { label:'Closed',       value:closedStage, color:'#64748B',bg:'#F1F5F9', stage:'CLOSED' },
                      ].map(s => (
                        <div key={s.label} onClick={() => setDrill({ stage: s.stage, label: s.label })}
                          style={{ background:s.bg, borderRadius:10, padding:'14px 16px',
                            borderTop:`3px solid ${s.color}`, cursor:'pointer', transition:'transform 0.15s,box-shadow 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 16px rgba(13,59,94,0.12)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                          <div style={{ fontSize:10, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>{s.label}</div>
                          <div style={{ fontSize:26, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1 }}>{s.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Pipeline progress bars */}
                    <div style={{ borderTop:`1px solid ${C.borderL}`, paddingTop:14 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>Distribution</div>
                      {[
                        { label:'Login',        value:loginStage,  color:C.purple },
                        { label:'Sanction',     value:sancStage,   color:C.skyB   },
                        { label:'Disbursement', value:disbStage,   color:C.greenB },
                        { label:'PDD',          value:pddStage,    color:C.navy   },
                        { label:'Revenue',      value:revStage,    color:C.tealB  },
                        { label:'Closed',       value:closedStage, color:'#94A3B8'},
                      ].map(s => <PBar key={s.label} label={s.label} value={s.value} total={totalCount} color={s.color}/>)}
                    </div>
                  </Card>
                </>
              )}
            </>
          )}

          {/* Table when rows available */}
          {rows.length > 0 && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
              overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
              <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC',
                display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <FiBarChart2 size={14} style={{ color:C.navy, opacity:0.7 }}/>
                  <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>Overall MIS — {monthLabel}</span>
                  <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:999,
                    fontSize:10.5, fontWeight:700, border:`1px solid rgba(13,59,94,0.12)` }}>{rows.length}</span>
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {['#','Executive','Leads','Login','Sanction','Disbursement','PDD','Revenue','Closed','Disb. Amount','Revenue Amount'].map((h,i)=>(
                        <th key={h} style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t2,
                          textTransform:'uppercase', letterSpacing:'0.07em',
                          textAlign:(i>=9)?'right':'left',
                          borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const isLast = idx === rows.length - 1;
                      const exec   = [r.internal_staff__first_name, r.internal_staff__last_name].filter(Boolean).join(' ') || '—';
                      const execInitials = exec.split(' ').map(p=>p[0]).slice(0,2).join('').toUpperCase()||'?';
                      return (
                        <tr key={r.internal_staff||idx}>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t4 }}>{idx+1}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <div style={{ width:24, height:24, borderRadius:'50%', background:C.navyL,
                                display:'flex', alignItems:'center', justifyContent:'center', color:C.navy, fontSize:9, fontWeight:800 }}>
                                {execInitials}
                              </div>
                              <span style={{ fontSize:12.5, fontWeight:600, color:C.t1 }}>{exec}</span>
                            </div>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontWeight:700, fontSize:13, color:C.t1 }}>{r.total_leads||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.login_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.sanction_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.disbursement_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.pdd_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.revenue_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.closed_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:800, color:C.greenB }}>{fmtAmt(r.total_disbursement)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:700, color:C.tealB }}>{fmtAmt(r.total_revenue)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {drill && (
        <StageDetailModal
          stage={drill.stage}
          stageLabel={drill.label}
          onClose={() => setDrill(null)}
        />
      )}
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   TAB 3 — PIPELINE MIS
   Real API: GET /api/leads/mis/pipeline/
   Response: { follow_up:4, login:2, sanction:0, disbursement:0, pdd:2, revenue:0, closed:1 }
   This is a SUMMARY object, not a list — rendered as visual pipeline.
═════════════════════════════════════════════════════════════════ */
function PipelineMIS() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [drill, setDrill]     = useState(null); // { stage, label }

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getMisPipeline();
      const d   = res?.data;
      if (d?.detail) throw new Error(d.detail);
      setData(d);
    } catch (e) {
      setError(e?.message || e?.detail || String(e) || 'Failed to load pipeline MIS');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Real API keys: follow_up, login, sanction, disbursement, pdd, revenue, closed
  const fu    = data?.follow_up    ?? 0;
  const login = data?.login        ?? 0;
  const sanc  = data?.sanction     ?? 0;
  const disb  = data?.disbursement ?? 0;
  const pdd   = data?.pdd          ?? 0;
  const rev   = data?.revenue      ?? 0;
  const closed= data?.closed       ?? 0;
  const total = fu + login + sanc + disb + pdd + rev + closed;
  const active= fu + login + sanc + disb + pdd + rev; // excluding closed

  const stageCards = [
    { label:'Follow-up',   value:fu,    color:C.goldB,  bg:C.goldL,   icon:<FiPhone size={13}/>,      pct: total > 0 ? Math.round(fu/total*100)     : 0, stage:'FOLLOW_UP' },
    { label:'Login',       value:login, color:C.purple, bg:C.purpleL, icon:<FiFileText size={13}/>,   pct: total > 0 ? Math.round(login/total*100)  : 0, stage:'LOGIN' },
    { label:'Sanction',    value:sanc,  color:C.skyB,   bg:C.skyL,    icon:<FiCheckCircle size={13}/>,pct: total > 0 ? Math.round(sanc/total*100)   : 0, stage:'SANCTION' },
    { label:'PDD',         value:pdd,   color:C.navy,   bg:C.navyL,   icon:<FiTarget size={13}/>,     pct: total > 0 ? Math.round(pdd/total*100)    : 0, stage:'PDD' },
    { label:'Revenue',     value:rev,   color:C.tealB,  bg:C.tealL,   icon:<FiTrendingUp size={13}/>, pct: total > 0 ? Math.round(rev/total*100)    : 0, stage:'REVENUE' },
  ];

  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const allLeads = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await api.getLeads({ page, page_size: 100 });
        const d = res?.data;
        const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
        allLeads.push(...results);
        hasMore = !!d?.next && results.length > 0;
        page++;
      }

      const stageMap = {
        FOLLOW_UP: 'Follow-up',
        LOGIN: 'Login',
        SANCTION: 'Sanction',
        DISBURSEMENT: 'Disbursement',
        PDD: 'PDD',
        REVENUE: 'Revenue',
        CLOSED: 'Closed',
      };

      exportCsv(
        ['Lead No','Customer','Mobile','Product','Institution','Stage','Login Date','Sanction Date','Expected Disb Date','Sanctioned Amount','Executive'],
        allLeads.map(l => {
          const exec = [l.created_by_detail?.first_name, l.created_by_detail?.last_name].filter(Boolean).join(' ')
            || l.created_by_name || '—';
          return [
            l.lead_no || `#${l.id}`,
            l.customer_name || '',
            l.mobile_number || '',
            l.product?.name || '',
            l.institution?.name || '',
            stageMap[l.current_stage] || l.status?.name || '',
            l.login_date || '',
            l.sanction_date || '',
            l.expected_disbursement_date || '',
            l.sanctioned_amount || '',
            exec,
          ];
        }),
        'pipeline_mis'
      );
    } catch (e) {
      toast.error('Failed to export pipeline data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {/* Top KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
        <Kpi label="Total Leads in System" value={loading?null:total}
          icon={<FiUsers size={15}/>} iconBg={C.navyL} iconColor={C.navy} accent={C.navy} loading={loading}/>
        <Kpi label="Active (In Pipeline)"  value={loading?null:active}
          icon={<FiActivity size={15}/>} iconBg={C.skyL} iconColor={C.skyB} accent={C.skyB} loading={loading}/>
        <Kpi label="Disbursed"             value={loading?null:disb}
          icon={<FiDollarSign size={15}/>} iconBg={C.greenL} iconColor={C.greenB} accent={C.greenB} loading={loading}
          onClick={() => setDrill({ stage:'DISBURSEMENT', label:'Disbursement' })}/>
        <Kpi label="Closed"                value={loading?null:closed}
          icon={<FiCheckCircle size={15}/>} iconBg='#F1F5F9' iconColor='#64748B' accent='#94A3B8' loading={loading}
          onClick={() => setDrill({ stage:'CLOSED', label:'Closed' })}/>
      </div>

      {loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:14 }}>
          </div>
        </div>
      )}
      {!loading && error && <Err msg={error} onRetry={load}/>}

      {!loading && !error && data !== null && (
        <>
          {/* Stage cards grid - all 7 stages in one grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            {stageCards.map(s => (
              <div key={s.label} onClick={() => s.stage && setDrill({ stage: s.stage, label: s.label })}
                style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
                  padding:'16px 18px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)',
                  borderTop:`3px solid ${s.color}`, position:'relative', overflow:'hidden',
                  cursor: s.stage ? 'pointer' : 'default', transition:'transform 0.15s,box-shadow 0.15s' }}
                onMouseEnter={e => { if(s.stage){e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 16px rgba(13,59,94,0.12)';} }}
                onMouseLeave={e => { if(s.stage){e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='';} }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
                  <div style={{ width:34, height:34, borderRadius:9, background:s.bg,
                    display:'flex', alignItems:'center', justifyContent:'center', color:s.color }}>{s.icon}</div>
                  <span style={{ fontSize:12, fontWeight:700, color:s.color, background:s.bg,
                    padding:'2px 8px', borderRadius:20 }}>{s.pct}%</span>
                </div>
                <div style={{ fontSize:28, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1, marginBottom:5 }}>{s.value}</div>
                <div style={{ fontSize:11.5, fontWeight:600, color:C.t3 }}>{s.label}</div>
                <div style={{ fontSize:10, color:C.t4, marginTop:3 }}>Click to view cases</div>
              </div>
            ))}
          </div>

          {/* Pipeline progress bars */}
          <Card title="Pipeline Distribution" icon={<FiBarChart2 size={13}/>}
            headerRight={
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={load}
                  style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                    background:C.white, color:C.t3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <FiRefreshCw size={13}/>
                </button>
                <button onClick={doExport} disabled={exporting}
                  style={{ display:'flex', alignItems:'center', gap:5, height:32, padding:'0 13px',
                    borderRadius:7, background:C.goldB, color:'#0D1B2A', border:'none', fontWeight:700,
                    fontSize:12, cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.7 : 1 }}>
                  {exporting
                    ? <><div style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(13,27,42,0.3)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} /> Exporting…</>
                    : <><FiDownload size={11}/> Export</>}
                </button>
              </div>
            }>
            {stageCards.map(s => (
              <PBar key={s.label} label={s.label} value={s.value} total={total} color={s.color}/>
            ))}
            <div style={{ paddingTop:14, borderTop:`1px solid ${C.borderL}`,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:12, color:C.t3 }}>Total leads across all stages</span>
              <span style={{ fontSize:18, fontWeight:800, color:C.navy }}>{total}</span>
            </div>
          </Card>
        </>
      )}

      {drill && (
        <StageDetailModal
          stage={drill.stage}
          stageLabel={drill.label}
          onClose={() => setDrill(null)}
        />
      )}
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   TAB 4 — USER-WISE MIS
   Real API: GET /api/leads/mis/user_wise/?year=YYYY&month=MM
   Response: { "detail": "year and month required" } if missing params
   Once supplied → actual shape handled gracefully.
═════════════════════════════════════════════════════════════════ */
function UserWiseMIS() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [year,  setYear]      = useState(String(curYear));
  const [month, setMonth]     = useState(String(curMonth));
  const [searched, setSearched] = useState(false);
  const [search, setSearch]   = useState('');

  const load = useCallback(async (y = year, m = month) => {
    if (!y || !m) { setError('Please select both year and month.'); return; }
    setLoading(true); setError(null); setSearched(true);
    try {
      // Use POST to incentive calculate API (same as Task-7)
      const res = await api.calculateIncentive({ year: Number(y), month: Number(m) });
      const d   = res?.data;
      if (d?.detail) throw new Error(d.detail);
      setData(d);
    } catch (e) {
      setError(e?.message || e?.detail || String(e) || 'Failed to load user-wise MIS');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  const rows = Array.isArray(data?.data) ? data.data
           : Array.isArray(data?.results) ? data.results
           : Array.isArray(data) ? data
           : [];

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    const ud = r.user_detail || {};
    const fn = ud.first_name || '';
    const ln = ud.last_name || '';
    const name = [fn, ln].filter(Boolean).join(' ');
    return name.toLowerCase().includes(q) || ud.email?.toLowerCase().includes(q);
  });

  // Fields from incentive calculate API
  const totalDisb   = rows.reduce((s,r) => s + Number(r.total_disbursement||0), 0);
  const totalRev    = rows.reduce((s,r) => s + Number(r.total_net_to_company||0), 0);
  const totalIncent = rows.reduce((s,r) => s + Number(r.incentive_amount||0), 0);

  const YEARS  = Array.from({ length: 5 }, (_, i) => String(curYear - i));
  const MONTHS = [
    { v:'01',l:'January'   }, { v:'02',l:'February'  }, { v:'03',l:'March'     },
    { v:'04',l:'April'     }, { v:'05',l:'May'        }, { v:'06',l:'June'      },
    { v:'07',l:'July'      }, { v:'08',l:'August'     }, { v:'09',l:'September' },
    { v:'10',l:'October'   }, { v:'11',l:'November'   }, { v:'12',l:'December'  },
  ];
  const monthLabel = year && month
    ? new Date(`${year}-${month}-01`).toLocaleString('en-IN', { month:'long', year:'numeric' })
    : '';

  const AVATAR_COLORS = ['#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B'];

  const [caseExporting, setCaseExporting] = useState(false);

  const doExport = () => {
    if (rows.length > 0) {
      exportCsv(
        ['User','Email','Disbursed','BSA Payout','Net to Company','Excess','Incentive'],
        rows.map(r => {
          const ud = r.user_detail || {};
          const fn = ud.first_name || '';
          const ln = ud.last_name || '';
          const name = [fn, ln].filter(Boolean).join(' ');
          return [
            name,
            ud.email || '',
            r.total_disbursement || '0.00',
            r.total_bsa_payout || '0.00',
            r.total_net_to_company || '0.00',
            r.excess_above_cutoff || '0.00',
            r.incentive_amount || '0.00'
          ];
        }),
        `user_wise_mis_${year}_${month}`
      );
    }
  };

  const doCaseExport = async () => {
    setCaseExporting(true);
    try {
      // Fetch all leads + disbursement records + revenue records in parallel
      const leadsPromise = (async () => {
        const all = [];
        let page = 1;
        while (true) {
          const res = await api.getLeads({ page, page_size: 100 });
          const d = res?.data;
          const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
          all.push(...results);
          if (!d?.next || results.length === 0) break;
          page++;
        }
        return all;
      })();

      const [allLeads, disbRes, revRes] = await Promise.all([
        leadsPromise,
        api.getLeadDisbursements({ page_size: 1000 }),
        api.getLeadRevenues({ page_size: 1000 }),
      ]);

      // Build disbursement map: lead_id → { latest date, total amount }
      const disbMap = {};
      const disbData = disbRes?.data?.results || disbRes?.data || [];
      disbData.forEach(d => {
        const key = String(d.lead);
        if (!disbMap[key]) disbMap[key] = { date: '', amount: 0 };
        if (d.disbursement_date && (!disbMap[key].date || d.disbursement_date > disbMap[key].date)) {
          disbMap[key].date = d.disbursement_date;
        }
        disbMap[key].amount += parseFloat(d.amount || 0);
      });

      // Build revenue map: lead_id → revenue record
      const revMap = {};
      const revData = revRes?.data?.results || revRes?.data || [];
      revData.forEach(r => { if (r.lead) revMap[String(r.lead)] = r; });

      // Build user name map from incentive rows
      const userMap = {};
      rows.forEach(r => {
        const ud = r.user_detail || {};
        const name = [ud.first_name, ud.last_name].filter(Boolean).join(' ') || ud.email || `User #${ud.id}`;
        if (ud.id) userMap[String(ud.id)] = name;
      });

      const stageMap = {
        FOLLOW_UP: 'Follow-up', LOGIN: 'Login', SANCTION: 'Sanction',
        DISBURSEMENT: 'Disbursement', PDD: 'PDD', REVENUE: 'Revenue', CLOSED: 'Closed',
      };

      exportCsv(
        ['User Name','Lead No','Customer','Product','Disb. Date','Disbursed Amount','Payout to Company','Revenue Received','Stage','PDD Status'],
        allLeads.map(l => {
          const key = String(l.id);
          const dr  = disbMap[key];
          const rr  = revMap[key];
          const cbId = typeof l.created_by === 'object' ? l.created_by?.id : l.created_by;
          const cdId = l.created_by_detail?.id;
          const exec = userMap[String(cbId)] || userMap[String(cdId)]
            || [l.created_by_detail?.first_name, l.created_by_detail?.last_name].filter(Boolean).join(' ')
            || '';
          const pddStatus = l.current_stage === 'PDD' ? (l.pdd_status || 'Pending')
            : l.current_stage === 'REVENUE' ? 'Cleared'
            : l.current_stage === 'CLOSED' ? 'N/A' : '—';
          return [
            exec,
            l.lead_no || `#${l.id}`,
            l.customer_name || '',
            l.product?.name || '',
            dr?.date || '',
            dr?.amount || '',
            rr?.payout_to_company || '',
            rr?.revenue_received || '',
            stageMap[l.current_stage] || l.current_stage || '',
            pddStatus,
          ];
        }),
        `user_wise_cases_${year}_${month}`
      );
    } catch (e) {
      toast.error('Failed to export case details');
    } finally {
      setCaseExporting(false);
    }
  };

  return (
    <>
      

      {/* Param selector */}
      <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
        padding:'16px 20px', marginBottom:14, boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
        <div style={{ fontSize:11.5, fontWeight:700, color:C.t3, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>Select Period</div>
        <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Year</label>
            <select value={year} onChange={e => setYear(e.target.value)}
              style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
                fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit', minWidth:100 }}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Month</label>
            <select value={month} onChange={e => setMonth(e.target.value)}
              style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
                fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit', minWidth:140 }}>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <button onClick={() => load(year, month)}
            style={{ height:36, padding:'0 20px', borderRadius:8, background:C.navy, color:'#fff',
              border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
              display:'flex', alignItems:'center', gap:6 }}>
            <FiFilter size={12}/> Load Report
          </button>
          {rows.length > 0 && (
            <>
              <button onClick={doExport}
                style={{ height:36, padding:'0 16px', borderRadius:8, background:C.goldB, color:'#0D1B2A',
                  border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                  display:'flex', alignItems:'center', gap:6 }}>
                <FiDownload size={12}/> Export Summary
              </button>
              <button onClick={doCaseExport} disabled={caseExporting}
                style={{ height:36, padding:'0 16px', borderRadius:8, background:C.tealB, color:'#fff',
                  border:'none', fontSize:13, fontWeight:700, cursor: caseExporting ? 'not-allowed' : 'pointer', fontFamily:'inherit',
                  display:'flex', alignItems:'center', gap:6, opacity: caseExporting ? 0.7 : 1 }}>
                {caseExporting
                  ? <><div style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} /> Exporting…</>
                  : <><FiDownload size={12}/> Export Case Details</>}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Not yet searched */}
      {!searched && !loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'48px 24px', textAlign:'center', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ width:52, height:52, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
            <FiUsers size={22} style={{ color:C.navy, opacity:0.5 }}/>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>Select a period to load User-wise MIS</div>
          <div style={{ fontSize:12.5, color:C.t3 }}>Choose year and month, then click "Load Report"</div>
        </div>
      )}

      {loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[0,1,2,3,4].map(i=><Sh key={i} h={52}/>)}
          </div>
        </div>
      )}

      {!loading && error && <Err msg={error} onRetry={() => load(year, month)}/>}

      {!loading && !error && searched && data !== null && (
        <>
          {/* KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            <Kpi label="Executives"    value={rows.length}          icon={<FiUsers size={15}/>}     iconBg={C.navyL}   iconColor={C.navy}  accent={C.navy}   loading={false} sub={monthLabel}/>
            <Kpi label="Total Disbursed" value={fmtAmt(totalDisb)} icon={<FiDollarSign size={15}/>} iconBg={C.greenL}  iconColor={C.greenB} accent={C.greenB} loading={false}/>
            <Kpi label="Total Revenue" value={fmtAmt(totalRev)}    icon={<FiTrendingUp size={15}/>} iconBg={C.tealL}   iconColor={C.tealB} accent={C.tealB}  loading={false}/>
            <Kpi label="Total Incentive" value={fmtAmt(totalIncent)} icon={<FiActivity size={15}/>} iconBg={C.goldL}   iconColor={C.gold}  accent={C.goldB}  loading={false}/>
          </div>

          {/* Table or summary */}
          {rows.length === 0 ? (
            <Card title={`User-wise MIS — ${monthLabel}`} icon={<FiUsers size={13}/>}>
              {/* Check if API returned a non-list aggregate */}
              {data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0 && !data.results ? (
                <div>
                  <div style={{ marginBottom:14, padding:'10px 14px', background:C.skyL, borderRadius:9, fontSize:12.5, color:C.sky, fontWeight:500 }}>
                    API returned an aggregate summary. Detailed per-user breakdown will be available when the backend exposes individual records.
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12 }}>
                    {Object.entries(data).map(([k,v]) => (
                      <div key={k} style={{ background:C.borderL, borderRadius:9, padding:'12px 14px' }}>
                        <div style={{ fontSize:10.5, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>{k.replace(/_/g,' ')}</div>
                        <div style={{ fontSize:16, fontWeight:700, color:C.t1 }}>{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Empty label={`No user data for ${monthLabel}`}/>
              )}
            </Card>
          ) : (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
              overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
              <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC',
                display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <FiUsers size={14} style={{ color:C.navy, opacity:0.7 }}/>
                  <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>User-wise — {monthLabel}</span>
                  <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:999,
                    fontSize:10.5, fontWeight:700, border:`1px solid rgba(13,59,94,0.12)` }}>{filtered.length}</span>
                </div>
                <div style={{ position:'relative' }}>
                  <FiSearch size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
                  <input type="text" placeholder="Search user…" value={search} onChange={e=>setSearch(e.target.value)}
                    style={{ height:32, border:`1px solid ${C.border}`, borderRadius:7, padding:'0 10px 0 28px',
                      fontSize:12.5, color:C.t1, background:C.bg, outline:'none', fontFamily:'inherit', width:180 }}
                    onFocus={e=>e.target.style.borderColor=C.navy} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {['#','Executive','Email','Disbursed','BSA Payout','Net to Co.','Excess','Incentive ₹'].map((h,i)=>(
                        <th key={h} style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t2,
                          textTransform:'uppercase', letterSpacing:'0.07em',
                          textAlign:i>=3?'right':'left',
                          borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, idx) => {
                      const isLast  = idx === filtered.length - 1;
                      const ud      = r.user_detail || {};
                      const fn      = ud.first_name || '';
                      const ln      = ud.last_name  || '';
                      const name    = [fn, ln].filter(Boolean).join(' ') || ud.email || `User #${r.id}` || '—';
                      const email   = ud.email || '';
                      const disbAmt = r.total_disbursement || '0.00';
                      const bsaPay  = r.total_bsa_payout || '0.00';
                      const netCo   = r.total_net_to_company || '0.00';
                      const excess  = r.excess_above_cutoff || '0.00';
                      const incent  = r.incentive_amount || '0.00';
                      const aColor  = AVATAR_COLORS[(name.charCodeAt(0)||0) % AVATAR_COLORS.length];
                      return (
                        <tr key={r.id||idx}>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t4 }}>{idx+1}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                              <div style={{ width:32, height:32, borderRadius:'50%', background:aColor+'20', border:`1.5px solid ${aColor}40`,
                                color:aColor, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:11, flexShrink:0 }}>
                                {name.split(' ').map(p=>p[0]).slice(0,2).join('').toUpperCase()||'?'}
                              </div>
                              <div>
                                <div style={{ fontWeight:700, fontSize:13, color:C.t1 }}>{name}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, color:C.t2 }}>{email || '—'}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:800, color:C.greenB }}>{fmtAmt(disbAmt)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, color:C.t2 }}>{fmtAmt(bsaPay)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:700, color:C.navy }}>{fmtAmt(netCo)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, color: Number(excess) > 0 ? C.greenB : C.t4 }}>{Number(excess) > 0 ? fmtAmt(excess) : '—'}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            {Number(incent) > 0
                              ? <span style={{ fontSize:13, fontWeight:800, color:C.gold, background:C.goldL, padding:'3px 10px', borderRadius:20, border:'1px solid #FDE68A' }}>{fmtAmt(incent)}</span>
                              : <span style={{ fontSize:12.5, color:C.t4 }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ padding:'9px 18px', borderTop:`1px solid ${C.borderL}`, fontSize:11.5, color:C.t4, background:'#FAFBFC' }}>
                Showing {filtered.length} of {rows.length} executives · {monthLabel}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   TAB 5 — DISBURSEMENT REPORT  (NEW-TASK B)
   Client requirement:
   - Date-wise filter: select month → show all Disb cases for that month
   - Filters: User Wise | Date Wise | Location Wise
   - Date Wise = date action was taken by user on App
═════════════════════════════════════════════════════════════════ */
function DisbursementReport() {
  const now2        = new Date();
  const [year,      setYear]      = useState(String(now2.getFullYear()));
  const [month,     setMonth]     = useState(String(now2.getMonth() + 1));
  const [userFilter,setUserFilter]= useState('');
  const [location,  setLocation]  = useState('');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [users,     setUsers]     = useState([]);
  const [records,   setRecords]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [searched,  setSearched]  = useState(false);
  const [error,     setError]     = useState(null);
  const [page,      setPage]      = useState(1);
  const [exporting, setExporting] = useState(false);
  const PAGE = 20;

  const YEARS  = Array.from({ length:5 }, (_,i) => String(now2.getFullYear() - i));
  const MONTHS = [
    {v:'1',l:'January'},{v:'2',l:'February'},{v:'3',l:'March'},{v:'4',l:'April'},
    {v:'5',l:'May'},{v:'6',l:'June'},{v:'7',l:'July'},{v:'8',l:'August'},
    {v:'9',l:'September'},{v:'10',l:'October'},{v:'11',l:'November'},{v:'12',l:'December'},
  ];

  // Load users for dropdown
  useEffect(() => {
    api.getUsers({ page_size:200 })
      .then(r => {
        const arr = Array.isArray(r?.data?.results) ? r.data.results
                  : Array.isArray(r?.data)           ? r.data : [];
        setUsers(arr);
      })
      .catch(() => {});
  }, []);

  const fmtDMY = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    } catch { return iso; }
  };

  const load = useCallback(async () => {
    if (!year || !month) { setError('Please select year and month'); return; }
    setLoading(true); setError(null); setSearched(true); setPage(1);
    try {
      const yNum = parseInt(year);
      const mNum = parseInt(month);
      // Month start/end for filtering
      const mStart = new Date(yNum, mNum - 1, 1);
      const mEnd   = new Date(yNum, mNum, 0, 23, 59, 59);

      // Fetch all disbursement records for this month
      const allDisb = [];
      let pg = 1;
      while (pg <= 20) {
        const res = await api.getLeadDisbursements({ page: pg, page_size:100 });
        const d = res?.data;
        const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
        allDisb.push(...results);
        if (!d?.next || results.length === 0) break;
        pg++;
      }

      // Filter by month (disbursement_date)
      const monthDisb = allDisb.filter(d => {
        const dt = new Date(d.disbursement_date || d.created_at || '');
        return dt >= mStart && dt <= mEnd;
      });

      // Fetch leads for these disbursement records
      const leadIds = [...new Set(monthDisb.map(d => d.lead).filter(Boolean))];
      const leadMap = {};
      await Promise.allSettled(
        leadIds.map(async id => {
          try {
            const r = await api.getLeadById(id);
            if (r?.data) leadMap[id] = r.data;
          } catch {}
        })
      );

      // Merge: one row per disbursement record
      let merged = monthDisb.map(d => ({
        ...d,
        _lead: leadMap[d.lead] || null,
      }));

      // Apply User filter
      if (userFilter) {
        merged = merged.filter(r => {
          const lead = r._lead;
          const cbId = lead?.created_by?.id || lead?.created_by || lead?.internal_staff;
          return String(cbId) === String(userFilter);
        });
      }

      // Apply Location filter (city field on lead)
      if (location.trim()) {
        const q = location.trim().toLowerCase();
        merged = merged.filter(r => {
          const lead = r._lead;
          const loc  = (lead?.city || lead?.location || lead?.address || '').toLowerCase();
          return loc.includes(q);
        });
      }

      // Apply Date-wise filter (specific date range override)
      if (dateFrom || dateTo) {
        merged = merged.filter(r => {
          const dt = new Date(r.disbursement_date || r.created_at || '');
          const from = dateFrom ? new Date(dateFrom) : null;
          const to   = dateTo   ? new Date(dateTo + 'T23:59:59') : null;
          if (from && dt < from) return false;
          if (to   && dt > to  ) return false;
          return true;
        });
      }

      setRecords(merged);
    } catch (e) {
      setError(e?.message || 'Failed to load disbursement report');
    } finally {
      setLoading(false);
    }
  }, [year, month, userFilter, location, dateFrom, dateTo]);

  const clearFilters = () => {
    setUserFilter(''); setLocation(''); setDateFrom(''); setDateTo('');
  };

  const monthLabel = year && month
    ? new Date(`${year}-${String(month).padStart(2,'0')}-01`)
        .toLocaleString('en-IN', { month:'long', year:'numeric' })
    : '';

  // Pagination
  const totalPages = Math.max(1, Math.ceil(records.length / PAGE));
  const visible    = records.slice((page-1)*PAGE, page*PAGE);

  // Summary stats
  const totalAmt  = records.reduce((s,r) => s + parseFloat(r.amount || 0), 0);

  // CSV export
  const doExport = async () => {
    setExporting(true);
    try {
      const userName = (r) => {
        const lead = r._lead;
        const u = lead?.created_by_detail || {};
        return [u.first_name, u.last_name].filter(Boolean).join(' ') || lead?.created_by_name || '—';
      };
      exportCsv(
        ['#','Lead No','Customer','Mobile','Product','Institution','Channel',
         'Disb Date','Disb Type','Disb Amount','Insurance Amt','Stage','User','Location','Lead Source'],
        records.map((r,i) => {
          const lead = r._lead || {};
          return [
            i+1,
            lead.lead_no || `#${r.lead}`,
            lead.customer_name || '',
            lead.mobile_number || '',
            lead.product?.name || '',
            lead.institution?.name || '',
            lead.channel?.name || '',
            r.disbursement_date || '',
            r.disbursement_type || '',
            r.amount || '',
            r.insurance_amount || '',
            lead.current_stage || '',
            userName(r),
            lead.city || lead.location || '',
            lead.lead_source || '',
          ];
        }),
        `disbursement_report_${year}_${month}`
      );
    } catch (e) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  const selSt = { height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
    fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit' };
  const inpSt = { ...selSt, minWidth:160 };

  return (
    <>
      {/* ── FILTER CARD ── */}
      <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
        padding:'18px 20px', marginBottom:14, boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>

        <div style={{ fontSize:11.5, fontWeight:700, color:C.t3, textTransform:'uppercase',
          letterSpacing:'0.08em', marginBottom:14 }}>Filters</div>

        {/* Row 1: Year + Month + User */}
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end', marginBottom:12 }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Year</label>
            <select value={year} onChange={e => setYear(e.target.value)} style={{ ...selSt, minWidth:100 }}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Month</label>
            <select value={month} onChange={e => setMonth(e.target.value)} style={{ ...selSt, minWidth:140 }}>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div style={{ flex:1, minWidth:180 }}>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>User Wise</label>
            <select value={userFilter} onChange={e => setUserFilter(e.target.value)} style={{ ...selSt, width:'100%' }}>
              <option value=''>All Users</option>
              {users.map(u => {
                const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `#${u.id}`;
                return <option key={u.id} value={u.id}>{name}</option>;
              })}
            </select>
          </div>
          <div style={{ flex:1, minWidth:160 }}>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Location Wise</label>
            <input type='text' placeholder='City / Area...' value={location}
              onChange={e => setLocation(e.target.value)}
              style={inpSt}
              onFocus={e=>e.target.style.borderColor=C.navy}
              onBlur={e=>e.target.style.borderColor=C.border}/>
          </div>
        </div>

        {/* Row 2: Date Wise (specific date range — overrides month) */}
        <div style={{ padding:'12px 14px', background:'#F8FAFC', borderRadius:9,
          border:`1px solid ${C.borderL}`, marginBottom:14 }}>
          <div style={{ fontSize:11.5, fontWeight:700, color:C.t3, textTransform:'uppercase',
            letterSpacing:'0.07em', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
            <FiCalendar size={12}/> Date Wise Filter
            <span style={{ fontSize:10.5, fontWeight:400, color:C.t4, textTransform:'none', letterSpacing:0 }}>
              (date action was taken by user on App)
            </span>
          </div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>From</label>
              <input type='date' value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ ...selSt, minWidth:150 }}/>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>To</label>
              <input type='date' value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ ...selSt, minWidth:150 }}/>
            </div>
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                style={{ height:36, padding:'0 12px', borderRadius:8, border:`1px solid ${C.redBord}`,
                  background:C.redL, color:C.redB, fontSize:12.5, fontWeight:600,
                  cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:5 }}>
                <FiX size={12}/> Clear Dates
              </button>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          <button onClick={load}
            style={{ height:38, padding:'0 22px', borderRadius:9, background:C.navy, color:'#fff',
              border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
              display:'flex', alignItems:'center', gap:7 }}>
            <FiSearch size={13}/> Load Report
          </button>
          {(userFilter || location || dateFrom || dateTo) && (
            <button onClick={() => { clearFilters(); }}
              style={{ height:38, padding:'0 16px', borderRadius:9, border:`1px solid ${C.border}`,
                background:C.white, color:C.t2, fontSize:13, fontWeight:600,
                cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
              <FiX size={12}/> Clear Filters
            </button>
          )}
          {searched && !loading && records.length > 0 && (
            <button onClick={doExport} disabled={exporting}
              style={{ height:38, padding:'0 16px', borderRadius:9, background:C.goldB,
                color:'#0D1B2A', border:'none', fontSize:13, fontWeight:700,
                cursor:exporting?'not-allowed':'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', gap:6, opacity:exporting?0.7:1 }}>
              {exporting
                ? <><div style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(13,27,42,0.3)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }}/> Exporting…</>
                : <><FiDownload size={12}/> Export CSV</>}
            </button>
          )}
        </div>
      </div>

      {/* ── NOT YET SEARCHED ── */}
      {!searched && !loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
          padding:'52px 24px', textAlign:'center', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:C.greenL,
            display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
            <FiDollarSign size={24} style={{ color:C.greenB, opacity:0.7 }}/>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>
            Select filters and click "Load Report"
          </div>
          <div style={{ fontSize:12.5, color:C.t3 }}>
            Shows all disbursement cases for the selected month with User, Date, Location filters
          </div>
        </div>
      )}

      {/* ── LOADING ── */}
      {loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[0,1,2,3,4].map(i => <Sh key={i} h={52}/>)}
          </div>
        </div>
      )}

      {/* ── ERROR ── */}
      {!loading && error && <Err msg={error} onRetry={load}/>}

      {/* ── RESULTS ── */}
      {!loading && !error && searched && (
        <>
          {/* KPI strip */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
            <Kpi label='Total Cases' value={records.length}
              icon={<FiFileText size={15}/>} iconBg={C.greenL} iconColor={C.greenB}
              accent={C.greenB} loading={false} sub={monthLabel}/>
            <Kpi label='Total Disbursed Amount' value={fmtAmt(totalAmt)}
              icon={<FiDollarSign size={15}/>} iconBg={C.navyL} iconColor={C.navy}
              accent={C.navy} loading={false}/>
            <Kpi label='Filters Active' value={[userFilter,location,dateFrom,dateTo].filter(Boolean).length || 'None'}
              icon={<FiFilter size={15}/>} iconBg={C.goldL} iconColor={C.gold}
              accent={C.goldB} loading={false} sub='User / Date / Location'/>
          </div>

          {/* Table */}
          {records.length === 0 ? (
            <Empty label={`No disbursement cases found for ${monthLabel}`}/>
          ) : (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
              overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>

              {/* Table header */}
              <div style={{ padding:'11px 18px', borderBottom:`1px solid ${C.borderL}`,
                background:'#FAFBFC', display:'flex', alignItems:'center',
                justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <FiDollarSign size={14} style={{ color:C.greenB, opacity:0.8 }}/>
                  <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>
                    Disbursement Cases — {monthLabel}
                  </span>
                  <span style={{ background:C.greenL, color:C.greenB, padding:'2px 8px',
                    borderRadius:999, fontSize:10.5, fontWeight:700,
                    border:`1px solid ${C.greenB}30` }}>{records.length}</span>
                </div>
                {totalPages > 1 && (
                  <span style={{ fontSize:12, color:C.t4 }}>
                    Page {page} / {totalPages}
                  </span>
                )}
              </div>

              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:980 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {['#','Lead No','Customer','Mobile','Product','Institution',
                        'Disb Date','Disb Type','Disb Amount','Ins. Amount',
                        'Stage','User','Location','Source'].map((h,i) => (
                        <th key={h} style={{ padding:'10px 13px', fontSize:10.5, fontWeight:800,
                          color:C.t2, textTransform:'uppercase', letterSpacing:'0.07em',
                          textAlign: i >= 8 && i <= 9 ? 'right' : 'left',
                          borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r, idx) => {
                      const lead   = r._lead || {};
                      const isLast = idx === visible.length - 1;
                      const uDet   = lead.created_by_detail || {};
                      const uName  = [uDet.first_name, uDet.last_name].filter(Boolean).join(' ')
                                  || lead.created_by_name || '—';
                      const DISB_TYPE_COLOR = {
                        FULL:    { bg:C.greenL, color:C.greenB },
                        PARTIAL: { bg:C.goldL,  color:'#92400E' },
                      };
                      const dtc = DISB_TYPE_COLOR[r.disbursement_type] || { bg:C.borderL, color:C.t3 };
                      return (
                        <tr key={r.id} style={{ background: idx%2===0 ? C.white : '#FAFBFC' }}
                          onMouseEnter={e=>e.currentTarget.style.background='#F0F9F4'}
                          onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?C.white:'#FAFBFC'}>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:12, color:C.t4 }}>{(page-1)*PAGE+idx+1}</td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <span style={{ fontSize:12, fontWeight:700, color:C.navy,
                              fontFamily:'monospace' }}>
                              {lead.lead_no || `#${r.lead}`}
                            </span>
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:13, fontWeight:600, color:C.t1 }}>
                            {lead.customer_name || '—'}
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:12, color:C.t2 }}>
                            {lead.mobile_number || '—'}
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:12, color:C.t2 }}>
                            {lead.product?.name || '—'}
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:12, color:C.t2 }}>
                            {lead.institution?.name || '—'}
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:12.5, fontWeight:600, color:C.t1 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                              <FiCalendar size={11} style={{ color:C.t4 }}/>
                              {fmtDMY(r.disbursement_date)}
                            </div>
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <span style={{ padding:'2px 9px', borderRadius:20, fontSize:11,
                              fontWeight:700, background:dtc.bg, color:dtc.color }}>
                              {r.disbursement_type || '—'}
                            </span>
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:800, color:C.greenB }}>
                              {r.amount ? fmtAmt(r.amount) : '—'}
                            </span>
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            textAlign:'right', fontSize:12.5, color:C.t2 }}>
                            {r.insurance_amount ? fmtAmt(r.insurance_amount) : '—'}
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700,
                              background: lead.current_stage==='DISBURSEMENT' ? C.greenL
                                        : lead.current_stage==='PDD'          ? C.navyL
                                        : lead.current_stage==='REVENUE'      ? C.tealL
                                        : lead.current_stage==='CLOSED'       ? C.borderL
                                        : C.goldL,
                              color: lead.current_stage==='DISBURSEMENT' ? C.greenB
                                   : lead.current_stage==='PDD'          ? C.navy
                                   : lead.current_stage==='REVENUE'      ? C.tealB
                                   : lead.current_stage==='CLOSED'       ? C.t3
                                   : C.gold }}>
                              {lead.current_stage || '—'}
                            </span>
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:12.5, fontWeight:600, color:C.t1 }}>
                            {uName}
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                            fontSize:12, color:C.t2 }}>
                            {lead.city || lead.location || '—'}
                          </td>

                          <td style={{ padding:'10px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            {lead.lead_source ? (
                              <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11,
                                fontWeight:700, background:C.borderL, color:C.t2 }}>
                                {lead.lead_source}
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding:'10px 18px', borderTop:`1px solid ${C.borderL}`,
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  background:'#FAFBFC' }}>
                  <span style={{ fontSize:12.5, color:C.t4 }}>
                    {(page-1)*PAGE+1}–{Math.min(page*PAGE,records.length)} of {records.length}
                  </span>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page<=1}
                      style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                        background:page<=1?C.borderL:C.white, color:page<=1?C.t4:C.t2,
                        cursor:page<=1?'not-allowed':'pointer',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <FiChevronLeft size={13}/>
                    </button>
                    {Array.from({length:totalPages},(_,i)=>i+1)
                      .filter(p => p===1 || p===totalPages || Math.abs(p-page)<=1)
                      .reduce((acc,p,i,arr) => {
                        if(i>0 && p-arr[i-1]>1) acc.push('…');
                        acc.push(p); return acc;
                      },[])
                      .map((p,i) => p==='…'
                        ? <span key={`e${i}`} style={{padding:'0 4px',color:C.t4,lineHeight:'32px'}}>…</span>
                        : <button key={p} onClick={() => setPage(p)}
                            style={{ width:32,height:32,borderRadius:7,
                              border:`1px solid ${p===page?C.navy:C.border}`,
                              background:p===page?C.navy:C.white,
                              color:p===page?'#fff':C.t2,
                              cursor:'pointer', fontSize:12, fontWeight:p===page?700:400 }}>{p}</button>
                      )}
                    <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page>=totalPages}
                      style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                        background:page>=totalPages?C.borderL:C.white, color:page>=totalPages?C.t4:C.t2,
                        cursor:page>=totalPages?'not-allowed':'pointer',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <FiChevronRight size={13}/>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   MAIN REPORTS PAGE — 5 TABS
═════════════════════════════════════════════════════════════════ */
const TABS = [
  { id:'disbursement', label:'Disbursement Report', icon:<FiDollarSign size={13}/>, color:C.green,  bg:C.greenL,  activeBg:'#15803D', shadow:'rgba(21,128,61,0.28)'   },
  { id:'last_month',   label:'Last Month MIS',      icon:<FiCalendar size={13}/>,  color:'#0369A1', bg:C.skyL,    activeBg:'#0369A1', shadow:'rgba(3,105,161,0.28)'   },
  { id:'overall',      label:'Overall MIS',         icon:<FiBarChart2 size={13}/>, color:C.navy,    bg:C.navyL,   activeBg:C.navy,    shadow:'rgba(13,59,94,0.28)'    },
  { id:'pipeline',     label:'Pipeline MIS',        icon:<FiTrendingUp size={13}/>,color:C.teal,    bg:C.tealL,   activeBg:C.teal,    shadow:'rgba(13,148,136,0.28)'  },
  { id:'user_wise',    label:'User-wise MIS',       icon:<FiUsers size={13}/>,     color:C.purple,  bg:C.purpleL, activeBg:'#6D28D9', shadow:'rgba(109,40,217,0.28)'  },
];

export default function Reports() {
  const [activeTab, setActiveTab] = useState('disbursement');

  return (
    <>
      <style>{`
        @keyframes rptShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes rptFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .rpt-anim { animation: rptFadeUp 0.22s ease both; }
        .rpt-tr:hover td { background:#F0F9F4 !important; }
      `}</style>

      <Layout pageTitle="Reports">
        <div className="rpt-anim" style={{ display:'flex', flexDirection:'column', gap:14,
          fontFamily:"'Inter',sans-serif", paddingBottom:28 }}>

          {/* Hero */}
          <div style={{ borderRadius:14, overflow:'hidden',
            background:'linear-gradient(120deg,#0D3B5E,#1A5276)',
            boxShadow:'0 4px 20px rgba(0,0,0,0.18)', position:'relative' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
              background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ padding:'20px 28px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.48)',
                marginBottom:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>Reports</div>
              <h2 style={{ margin:0, color:'#fff', fontSize:20, fontWeight:800,
                letterSpacing:'-0.03em', lineHeight:1.2 }}>MIS Reports</h2>
              <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>
                Monthly Information System — Disbursements, pipeline, revenue & incentive tracking
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {TABS.map(t => {
              const isAct = activeTab === t.id;
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px',
                    border:'none', borderRadius:10, cursor:'pointer', fontFamily:'inherit',
                    fontSize:13, transition:'all 0.15s',
                    background: isAct ? t.activeBg : t.bg,
                    color:      isAct ? '#fff' : t.color,
                    fontWeight: isAct ? 700 : 600,
                    boxShadow:  isAct ? `0 3px 10px ${t.shadow}` : 'none',
                    transform:  isAct ? 'translateY(-1px)' : 'none' }}
                  onMouseEnter={e=>{ if(!isAct){ e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow=`0 3px 10px ${t.shadow}`; }}}
                  onMouseLeave={e=>{ if(!isAct){ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='none'; }}}>
                  {t.icon} {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div key={activeTab}>
            {activeTab === 'disbursement' && <DisbursementReport/>}
            {activeTab === 'last_month'   && <LastMonthMIS/>}
            {activeTab === 'overall'      && <OverallMIS/>}
            {activeTab === 'pipeline'     && <PipelineMIS/>}
            {activeTab === 'user_wise'    && <UserWiseMIS/>}
          </div>

        </div>
      </Layout>
    </>
  );
}